import { getStoredLegacyToken } from './legacyAuthService';
import { LegacyOrganization } from '../types/legacy';

export interface CreatorRegistrationPayload {
  fullName: string;
  email: string;
  username: string;
  organization?: string;
  notes?: string;
  requestType?: 'edu' | 'creator';
}

export interface CreatorRegistrationRequest {
  id: string;
  fullName: string;
  email: string;
  username: string;
  organization?: string;
  notes?: string;
  requestType?: 'edu' | 'creator';
  status: 'pending' | 'approved' | 'activated' | 'rejected';
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNote?: string;
  activatedBy?: string;
  activatedAt?: string;
  temporaryPassword?: string;
  createdAt?: string;
  activatedOrganizationId?: number | null;
  activatedOrganizationName?: string;
  activatedOrganizationCreatedNow?: boolean;
}

function buildHeaders(includeAuth = false): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  if (includeAuth) {
    const rawToken =
      getStoredLegacyToken()
      || localStorage.getItem('legacy_token')
      || sessionStorage.getItem('legacy_token')
      || '';
    if (rawToken) {
      headers.Authorization = /^Bearer\s+/i.test(rawToken) ? rawToken : `Bearer ${rawToken}`;
    }
  }

  return headers;
}

async function parseResponse<T>(response: Response): Promise<T> {
  const rawText = await response.text();
  let payload: unknown = null;
  try {
    payload = rawText ? JSON.parse(rawText) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const structuredError =
      (payload && typeof payload === 'object' && 'error' in payload && typeof (payload as { error?: unknown }).error === 'string')
        ? String((payload as { error: string }).error)
        : '';
    const message = structuredError || rawText || `API error (${response.status})`;
    throw new Error(message);
  }

  return (payload as T) || ({} as T);
}

export async function submitCreatorRegistration(payload: CreatorRegistrationPayload): Promise<{ success: boolean; requestId: string }> {
  const response = await fetch('/api/creator-registration/request', {
    method: 'POST',
    headers: buildHeaders(false),
    body: JSON.stringify({
      ...payload,
      requestType: payload.requestType || 'edu',
    }),
  });

  return parseResponse<{ success: boolean; requestId: string }>(response);
}

export async function listCreatorRegistrationRequests(status: 'all' | 'pending' | 'approved' | 'activated' | 'rejected' = 'all'):
Promise<{ success: boolean; requests: CreatorRegistrationRequest[] }> {
  const query = status === 'all' ? '' : `?status=${status}`;
  const response = await fetch(`/api/creator-registration/list${query}`, {
    method: 'GET',
    headers: buildHeaders(true),
  });

  return parseResponse<{ success: boolean; requests: CreatorRegistrationRequest[] }>(response);
}

export async function decideCreatorRegistrationRequest(payload: {
  requestId: string;
  decision: 'approved' | 'rejected';
  note?: string;
}): Promise<{ success: boolean }> {
  const response = await fetch('/api/creator-registration/decision', {
    method: 'POST',
    headers: buildHeaders(true),
    body: JSON.stringify(payload),
  });

  return parseResponse<{ success: boolean }>(response);
}

export async function activateCreatorRegistrationRequest(payload: {
  requestId: string;
  note?: string;
  temporaryPassword?: string;
  organizationId?: number | null;
  organizationName?: string;
  createOrganization?: boolean;
}): Promise<{ success: boolean }> {
  const response = await fetch('/api/creator-registration/activate', {
    method: 'POST',
    headers: buildHeaders(true),
    body: JSON.stringify(payload),
  });

  return parseResponse<{ success: boolean }>(response);
}

function normalizeOrganizationList(payload: unknown): LegacyOrganization[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload as LegacyOrganization[];
  if (typeof payload === 'object') {
    const target = payload as { data?: unknown; items?: unknown; content?: unknown };
    if (Array.isArray(target.data)) return target.data as LegacyOrganization[];
    if (Array.isArray(target.items)) return target.items as LegacyOrganization[];
    if (Array.isArray(target.content)) return target.content as LegacyOrganization[];
  }
  return [];
}

export async function listLegacyOrganizations(): Promise<LegacyOrganization[]> {
  const response = await fetch('/api/organization/list?page=0&size=500', {
    method: 'GET',
    headers: buildHeaders(true),
  });
  const payload = await parseResponse<unknown>(response);
  return normalizeOrganizationList(payload);
}
